import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Layers, List } from "lucide-react";

export type KeyTerm = { term: string; definition: string };

/**
 * Key terms as a list, or as a flip deck. The list is the study sheet;
 * cards are for drilling one term at a time.
 */
export default function StudentKeyTerms({
  terms,
  heading = true,
}: {
  terms: KeyTerm[];
  heading?: boolean;
}) {
  const [view, setView] = useState<"list" | "cards">("list");
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    setFlipped(false);
  }, [index, view]);

  if (!terms.length) return null;

  const safeIndex = Math.min(index, terms.length - 1);
  const card = terms[safeIndex];
  const term = card?.term.trim() || "Untitled term";
  const definition = card?.definition.trim() || "—";

  const go = (next: number) => {
    const last = terms.length - 1;
    setIndex(next < 0 ? last : next > last ? 0 : next);
  };

  return (
    <div className="student-section student-terms">
      <div className="student-section__head">
        {heading ? <h2>Key terms</h2> : null}
        <div className="student-viewtabs" role="tablist" aria-label="Key terms view">
          <button
            type="button"
            role="tab"
            aria-selected={view === "list"}
            className={`student-viewtab${view === "list" ? " is-on" : ""}`}
            onClick={() => setView("list")}
          >
            <List size={13} aria-hidden />
            List
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === "cards"}
            className={`student-viewtab${view === "cards" ? " is-on" : ""}`}
            onClick={() => setView("cards")}
          >
            <Layers size={13} aria-hidden />
            Cards
          </button>
        </div>
      </div>

      {view === "list" ? (
        <div className="student-reading">
          <dl>
            {terms.map((row, i) => (
              <div key={`${row.term}-${i}`}>
                <dt>{row.term.trim() || "Untitled term"}</dt>
                <dd>{row.definition.trim() || "—"}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : (
        <div className="student-flash">
          <div className="student-flash__nav">
            <button
              type="button"
              className="student-flash__arrow"
              disabled={terms.length < 2}
              onClick={() => go(safeIndex - 1)}
              aria-label="Previous term"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="student-flash__count">
              {safeIndex + 1} / {terms.length}
            </span>
            <button
              type="button"
              className="student-flash__arrow"
              disabled={terms.length < 2}
              onClick={() => go(safeIndex + 1)}
              aria-label="Next term"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <button
            type="button"
            className={`student-flash__card${flipped ? " is-flipped" : ""}`}
            onClick={() => setFlipped((on) => !on)}
            aria-pressed={flipped}
            aria-label={
              flipped
                ? `Definition of ${term}. Activate to show the term.`
                : `${term}. Activate to show the definition.`
            }
          >
            <span className="student-flash__face student-flash__face--front">
              <span className="student-flash__hint">Term</span>
              <strong className={`student-flash__term student-flash__term--${faceSize(term)}`}>
                {term}
              </strong>
              <span className="student-flash__nudge">Tap to flip</span>
            </span>
            <span className="student-flash__face student-flash__face--back">
              <span className="student-flash__ruled" aria-hidden />
              <span className="student-flash__hint">{term}</span>
              <span className={`student-flash__def student-flash__def--${faceSize(definition)}`}>
                {definition}
              </span>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

function faceSize(text: string): "lg" | "md" | "sm" {
  const t = String(text || "").trim();
  const words = t.split(/\s+/).filter(Boolean).length;
  if (t.length > 160 || words > 28) return "sm";
  if (t.length > 48 || words > 8) return "md";
  return "lg";
}
